/**
 * Product Information Audit Trail Component
 * 
 * Displays comprehensive audit trail for product information changes
 * with filtering, pagination, and detailed change views.
 */

import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import {
  ProductInfoAudit,
  ProductInfoChangeType,
  AuditTrailQuery,
  AuditTrailResponse
} from '../types/enhanced-product-info.types';
import { formatBatchName } from '../lib/utils';
import { productInfoAuditService } from '../services/product-info-audit.service';

interface ProductInfoAuditTrailProps {
  batchId: string;
  onClose?: () => void;
}

interface AuditFilters {
  changeType?: ProductInfoChangeType;
  startDate?: string;
  endDate?: string;
  changedBy?: string;
}

export const ProductInfoAuditTrail: React.FC<ProductInfoAuditTrailProps> = ({
  batchId,
  onClose
}) => {
  const [auditData, setAuditData] = useState<AuditTrailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<AuditFilters>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedAudit, setExpandedAudit] = useState<string | null>(null);

  useEffect(() => {
    loadAuditTrail();
  }, [batchId, filters, currentPage]);

  const loadAuditTrail = async () => {
    try {
      setLoading(true);
      setError(null);

      const query: AuditTrailQuery = {
        batchId,
        page: currentPage,
        limit: 20,
        ...filters,
        startDate: filters.startDate ? new Date(filters.startDate) : undefined,
        endDate: filters.endDate ? new Date(filters.endDate) : undefined
      };

      const response = await productInfoAuditService.getAuditTrail(query);
      setAuditData(response);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit trail');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters: Partial<AuditFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const clearFilters = () => {
    setFilters({});
    setCurrentPage(1);
  };

  const getChangeTypeColor = (changeType: ProductInfoChangeType): string => {
    const colors = {
      images: 'bg-blue-100 text-blue-800',
      nutrition: 'bg-green-100 text-green-800',
      custom_content: 'bg-purple-100 text-purple-800',
      manufacturing: 'bg-orange-100 text-orange-800',
      label_design: 'bg-indigo-100 text-indigo-800',
      rollback: 'bg-red-100 text-red-800'
    };
    return colors[changeType] || 'bg-gray-100 text-gray-800';
  };

  const getChangeTypeIcon = (changeType: ProductInfoChangeType): string => {
    const icons = {
      label_design: 'LD',
      images: '🖼️',
      nutrition: '🥗',
      custom_content: '📝',
      manufacturing: '🏭',
      rollback: '↩️'
    };
    return icons[changeType] || '📋';
  };

  const formatChangeValue = (value: any): string => {
    if (value === null || value === undefined) {
      return 'None';
    }
    
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    
    return String(value);
  };

  const renderAuditDetails = (audit: ProductInfoAudit) => {
    return (
      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Old Values */}
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Previous Values</h4>
            <pre className="text-sm text-gray-600 bg-white p-3 rounded border overflow-auto max-h-40">
              {formatChangeValue(audit.oldValues)}
            </pre>
          </div>

          {/* New Values */}
          <div>
            <h4 className="font-medium text-gray-900 mb-2">New Values</h4>
            <pre className="text-sm text-gray-600 bg-white p-3 rounded border overflow-auto max-h-40">
              {formatChangeValue(audit.newValues)}
            </pre>
          </div>
        </div>

        {/* Additional Metadata */}
        {audit.changer && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <span>
                <strong>Changed by:</strong> {audit.changer.fullName} ({audit.changer.email})
              </span>
              {audit.batch && (
                <span>
                  <strong>Product:</strong>{' '}
                  {formatBatchName(audit.batch.productName, audit.batch.createdAt, audit.batch.batchTitle)}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading audit trail...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <div className="flex items-center">
          <span className="text-red-600 font-medium">Error:</span>
          <span className="ml-2 text-red-700">{error}</span>
        </div>
        <button
          onClick={loadAuditTrail}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            Product Information Audit Trail
          </h2>
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Change Type Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Change Type
            </label>
            <select
              value={filters.changeType || ''}
              onChange={(e) => handleFilterChange({ 
                changeType: e.target.value as ProductInfoChangeType || undefined 
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Types</option>
              <option value="images">Images</option>
              <option value="nutrition">Nutrition</option>
              <option value="custom_content">Custom Content</option>
              <option value="manufacturing">Manufacturing</option>
              <option value="label_design">Label Design</option>
              <option value="rollback">Rollback</option>
            </select>
          </div>

          {/* Start Date Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={filters.startDate || ''}
              onChange={(e) => handleFilterChange({ startDate: e.target.value || undefined })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* End Date Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={filters.endDate || ''}
              onChange={(e) => handleFilterChange({ endDate: e.target.value || undefined })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Clear Filters */}
          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="w-full px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Audit Trail List */}
      <div className="px-6 py-4">
        {auditData && auditData.auditTrail.length > 0 ? (
          <div className="space-y-4">
            {auditData.auditTrail.map((audit) => (
              <div
                key={audit.id}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">{getChangeTypeIcon(audit.changeType)}</span>
                    <div>
                      <div className="flex items-center space-x-2">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${getChangeTypeColor(audit.changeType)}`}
                        >
                          {audit.changeType.replace('_', ' ').toUpperCase()}
                        </span>
                        <span className="text-sm text-gray-600">
                          {format(new Date(audit.changedAt), 'MMM dd, yyyy HH:mm')}
                        </span>
                      </div>
                      {audit.changer && (
                        <div className="text-sm text-gray-600 mt-1">
                          by {audit.changer.fullName}
                        </div>
                      )}
                    </div>
                  </div>

                  <button
                    onClick={() => setExpandedAudit(
                      expandedAudit === audit.id ? null : audit.id
                    )}
                    className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                  >
                    {expandedAudit === audit.id ? 'Hide Details' : 'View Details'}
                  </button>
                </div>

                {expandedAudit === audit.id && renderAuditDetails(audit)}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No audit records found for the selected criteria.
          </div>
        )}
      </div>

      {/* Pagination */}
      {auditData && auditData.pagination.totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {((auditData.pagination.page - 1) * auditData.pagination.limit) + 1} to{' '}
              {Math.min(
                auditData.pagination.page * auditData.pagination.limit,
                auditData.pagination.totalCount
              )}{' '}
              of {auditData.pagination.totalCount} records
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={!auditData.pagination.hasPrevious}
                className="px-3 py-1 bg-gray-100 text-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200"
              >
                Previous
              </button>

              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded">
                Page {auditData.pagination.page} of {auditData.pagination.totalPages}
              </span>

              <button
                onClick={() => setCurrentPage(prev => prev + 1)}
                disabled={!auditData.pagination.hasNext}
                className="px-3 py-1 bg-gray-100 text-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
